import { Link } from 'react-router-dom';
import { ModelCardData } from '@/types/models';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Eye, GitFork, Lock, Globe } from 'lucide-react';
import { m } from 'framer-motion';

interface ModelCardProps {
  model: ModelCardData;
  showAuthor?: boolean;
  showVisibility?: boolean;
}

export function ModelCard({
  model,
  showAuthor = true,
  showVisibility = false,
}: ModelCardProps) {
  const modelUrl = `/${model.author.username}/${model.slug}`;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 1) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCount = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  const initials = model.author.username.slice(0, 2).toUpperCase();

  return (
    <m.div
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
      }}
    >
      <Link to={modelUrl} className="group block">
        <Card className="overflow-hidden transition-colors hover:border-muted-foreground/50">
          {/* Thumbnail */}
          <div className="relative aspect-square overflow-hidden bg-muted">
            {model.thumbnail_url ? (
              <img
                src={model.thumbnail_url}
                alt={model.title}
                className="h-full w-full object-cover transition-transform group-hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <div className="text-muted-foreground/50">
                  <svg
                    className="h-12 w-12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                  >
                    <path d="M12 2L2 7l10 5 10-5-10-5z" />
                    <path d="M2 17l10 5 10-5" />
                    <path d="M2 12l10 5 10-5" />
                  </svg>
                </div>
              </div>
            )}

            {/* Visibility badge */}
            {showVisibility && (
              <div className="absolute right-2 top-2">
                <Badge
                  variant="secondary"
                  className="bg-background/80 backdrop-blur-sm"
                >
                  {model.is_public ? (
                    <>
                      <Globe className="mr-1 h-3 w-3" /> Public
                    </>
                  ) : (
                    <>
                      <Lock className="mr-1 h-3 w-3" /> Private
                    </>
                  )}
                </Badge>
              </div>
            )}
          </div>

          <CardContent className="p-4">
            {/* Title */}
            <h3 className="mb-2 truncate text-sm font-medium text-foreground">
              {model.title}
            </h3>

            {/* Description */}
            {model.description && (
              <p className="mb-3 line-clamp-2 text-xs text-muted-foreground">
                {model.description}
              </p>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between">
              {/* Author */}
              {showAuthor && (
                <Link
                  to={`/${model.author.username}`}
                  className="flex items-center gap-2 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Avatar className="h-5 w-5">
                    <AvatarImage
                      src={model.author.avatar_url || undefined}
                      alt={model.author.username}
                    />
                    <AvatarFallback className="bg-muted text-[10px] text-muted-foreground">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs text-muted-foreground">
                    {model.author.username}
                  </span>
                </Link>
              )}

              {/* Stats */}
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1" title="Views">
                  <Eye className="h-3 w-3" />
                  {formatCount(model.view_count)}
                </span>
                <span className="flex items-center gap-1" title="Forks">
                  <GitFork className="h-3 w-3" />
                  {formatCount(model.fork_count)}
                </span>
                <span title={new Date(model.updated_at).toLocaleString()}>
                  {formatDate(model.updated_at)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    </m.div>
  );
}

// Grid component for displaying multiple cards
interface ModelGridProps {
  models: ModelCardData[];
  showAuthor?: boolean;
  showVisibility?: boolean;
  emptyMessage?: string;
}

export function ModelGrid({
  models,
  showAuthor = true,
  showVisibility = false,
  emptyMessage = 'No models found',
}: ModelGridProps) {
  if (models.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 text-muted-foreground/50">
          <svg
            className="mx-auto h-12 w-12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          >
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
        </div>
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <m.div
      className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.1,
          },
        },
      }}
      initial="hidden"
      animate="visible"
    >
      {models.map((model) => (
        <ModelCard
          key={model.id}
          model={model}
          showAuthor={showAuthor}
          showVisibility={showVisibility}
        />
      ))}
    </m.div>
  );
}
